<template>
  <div class="p-6 max-w-4xl mx-auto">
    <div class="mb-8 flex items-center justify-between">
      <div>
        <h1 class="text-3xl font-bold text-gray-800 dark:text-white flex items-center">
          <Icon icon="mdi:shield-key" class="mr-3 text-indigo-600" />
          Administración de Licencia
        </h1>
        <p class="text-gray-600 dark:text-gray-400 mt-2">
          Gestione la validez y el estado del sistema. Solo accesible para Super Administradores.
        </p>
      </div>
      <div class="bg-indigo-100 dark:bg-indigo-900/30 p-4 rounded-2xl flex items-center shadow-sm border border-indigo-200 dark:border-indigo-800">
        <div :class="status.activa ? 'bg-green-500' : 'bg-red-500'" class="h-4 w-4 rounded-full mr-3 animate-pulse"></div>
        <span class="font-bold text-indigo-900 dark:text-indigo-100">
          Estado: {{ status.activa ? 'Activa' : 'Bloqueada' }}
        </span>
      </div>
    </div>

    <div class="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
      <!-- Tarjeta Vencimiento -->
      <div class="bg-white dark:bg-gray-800 p-6 rounded-2xl shadow-xl border border-gray-100 dark:border-gray-700">
        <div class="text-gray-500 dark:text-gray-400 text-sm font-medium mb-1">Vencimiento</div>
        <div class="text-2xl font-bold text-gray-900 dark:text-white">
          {{ formatDate(status.vencimiento) }}
        </div>
        <div class="text-xs mt-2" :class="status.expirada ? 'text-red-500' : 'text-green-500'">
          {{ status.expirada ? 'Licencia expirada' : 'Licencia vigente' }}
        </div>
      </div>

      <!-- Tarjeta Días Restantes -->
      <div class="bg-white dark:bg-gray-800 p-6 rounded-2xl shadow-xl border border-gray-100 dark:border-gray-700">
        <div class="text-gray-500 dark:text-gray-400 text-sm font-medium mb-1">Días Restantes</div>
        <div class="text-4xl font-black" :class="status.dias_restantes < 5 ? 'text-red-500' : 'text-indigo-600'">
          {{ status.dias_restantes }}
        </div>
      </div>

      <!-- Tarjeta ID Sistema -->
      <div class="bg-white dark:bg-gray-800 p-6 rounded-2xl shadow-xl border border-gray-100 dark:border-gray-700">
        <div class="text-gray-500 dark:text-gray-400 text-sm font-medium mb-1">ID del Sistema</div>
        <div class="text-lg font-mono font-bold text-gray-700 dark:text-gray-300">
          SYSTEM-JJ-PRO-01
        </div>
      </div>
    </div>

    <div class="bg-white dark:bg-gray-800 rounded-3xl shadow-2xl overflow-hidden border border-gray-100 dark:border-gray-700">
      <div class="p-8">
        <h3 class="text-xl font-bold text-gray-800 dark:text-white mb-6 flex items-center">
          <Icon icon="mdi:calendar-edit" class="mr-2 text-indigo-500" />
          Actualizar Vigencia Manualmente
        </h3>

        <div class="space-y-6">
          <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Nueva Fecha de Vencimiento
              </label>
              <input 
                type="datetime-local" 
                v-model="formData.vencimiento"
                class="w-full bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-600 rounded-xl px-4 py-3 focus:ring-2 focus:ring-indigo-500 outline-none transition-all dark:text-white"
              />
            </div>
            <div class="flex items-end">
              <label class="relative inline-flex items-center cursor-pointer mb-3">
                <input type="checkbox" v-model="formData.activa" class="sr-only peer">
                <div class="w-14 h-7 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-indigo-300 dark:peer-focus:ring-indigo-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:left-[4px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-6 after:w-6 after:transition-all dark:border-gray-600 peer-checked:bg-indigo-600"></div>
                <span class="ml-3 text-sm font-medium text-gray-700 dark:text-gray-300">Licencia Activa</span>
              </label>
            </div>
          </div>

          <div class="pt-4 border-t border-gray-100 dark:border-gray-700 flex justify-end">
            <button 
              @click="guardarCambios"
              :disabled="loading"
              class="bg-indigo-600 hover:bg-indigo-700 text-white font-bold py-3 px-8 rounded-xl shadow-lg shadow-indigo-500/30 transition-all flex items-center disabled:opacity-50"
            >
              <Icon v-if="loading" icon="mdi:loading" class="animate-spin mr-2" />
              <Icon v-else icon="mdi:content-save" class="mr-2" />
              {{ loading ? 'Guardando...' : 'Aplicar Cambios' }}
            </button>
          </div>
        </div>
      </div>
      
      <div class="bg-amber-50 dark:bg-amber-900/20 p-6 border-t border-amber-100 dark:border-amber-900/30">
        <div class="flex">
          <Icon icon="mdi:alert" class="text-amber-500 text-2xl mr-3 flex-shrink-0" />
          <div>
            <h4 class="text-amber-800 dark:text-amber-200 font-bold">Atención</h4>
            <p class="text-amber-700 dark:text-amber-300 text-sm mt-1">
              Al cambiar estas configuraciones, el acceso de los clientes al sistema se verá afectado de inmediato. 
              Asegúrese de poner la fecha y hora correctas según el acuerdo de mensualidad.
            </p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, reactive } from 'vue';
import { Icon } from '@iconify/vue';
import api from '@/api';
import Swal from 'sweetalert2';

const status = ref({
  activa: true,
  vencimiento: null,
  expirada: false,
  dias_restantes: 0
});

const formData = reactive({
  vencimiento: '',
  activa: true
});

const loading = ref(false);

const loadStatus = async () => {
  try {
    const data = await api.apiFetch('/Configuracion/licencia-status');
    status.value = data;
    
    if (data.vencimiento) {
      // Formatear para input datetime-local
      const date = new Date(data.vencimiento);
      const tzoffset = date.getTimezoneOffset() * 60000;
      const localISOTime = (new Date(date - tzoffset)).toISOString().slice(0, 16);
      formData.vencimiento = localISOTime;
    }
    formData.activa = data.activa;
  } catch (error) {
    console.error('Error cargando estado de licencia:', error);
  }
};

const formatDate = (dateStr) => {
  if (!dateStr) return 'N/A';
  const date = new Date(dateStr);
  return date.toLocaleString('es-VE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const guardarCambios = async () => {
  if (!formData.vencimiento) {
    Swal.fire('Error', 'Por favor seleccione una fecha de vencimiento', 'error');
    return;
  }

  loading.value = true;
  try {
    await api.apiFetch('/Configuracion/actualizar-licencia-manual', {
      method: 'POST',
      body: {
        vencimiento: formData.vencimiento,
        activa: formData.activa
      }
    });

    await Swal.fire({
      icon: 'success',
      title: 'Cambios Guardados',
      text: 'La licencia ha sido actualizada correctamente.',
      timer: 2000
    });

    await loadStatus();
  } catch (error) {
    Swal.fire('Error', error.message || 'Error al actualizar licencia', 'error');
  } finally {
    loading.value = false;
  }
};

onMounted(() => {
  loadStatus();
});
</script>
